import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
  RefreshControl,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Colors } from '@/constants/colors';
import { getGroupArtifacts } from '@/services/artifacts/artifactService';
import { getGroupMessages, Message } from '@/services/chat/chatService';
import { marketSentimentService } from '@/services/market/marketSentimentService';
import { leaderboardService, LeaderboardUser } from '@/services/analytics/leaderboardService';
import { historicalSentimentService } from '@/services/analytics/historicalSentimentService';
import { SentimentDataPoint } from '@/types/historical-sentiment.types';
import { PulseOnboardingOverlay } from './PulseOnboardingOverlay';
import { SentimentHistoryChart, TimePeriod } from './SentimentHistoryChart';

interface PulseTabProps {
  groupId: string;
  onTickerPress: (ticker: string) => void;
}

interface PulseData {
  groupSentiment: number | null; // 0-100, null if no activity
  marketSentiment: number; // 0-100
  edge: number | null; // Difference (can be negative), null if no activity
  trendingStocks: { ticker: string; mentions: number }[];
  topContributors: LeaderboardUser[];
  groupRank: number | null;
  temperature: number; // 0-3 (🔥 count)
  streak: number; // Days with activity
  alpha: number; // Performance vs market (placeholder)
}

/**
 * PulseTab - Ultra-compact single-screen design
 * Following Steve Jobs & Elon Musk principles: ruthless simplification
 *
 * Layout (total ~372px):
 * - Header (40px): Title, subtitle, rank, temperature inline
 * - Trinity Boxes (80px): Group | Market | Edge comparison with sentiment labels
 * - Trending Stocks (~44px): Horizontal scrollable chips
 * - Leaderboard (~132px): Dense top-3 only, inline stats
 * - Performance Strip (32px): Streak and alpha in two lines
 * - Upgrade CTA (48px): Single clear action
 */
export const PulseTab: React.FC<PulseTabProps> = ({ groupId, onTickerPress }) => {
  const [pulse, setPulse] = useState<PulseData | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [historicalData, setHistoricalData] = useState<{
    dataPoints: SentimentDataPoint[];
    averageEdge: number | null;
  } | null>(null);
  const [selectedPeriod, setSelectedPeriod] = useState<TimePeriod>('1W');

  // Check if user has seen onboarding (temporarily disabled - will add help button instead)
  // useEffect(() => {
  //   checkOnboarding();
  // }, []);

  useEffect(() => {
    loadPulseData();
  }, [groupId]);

  // Update timestamp display every minute
  useEffect(() => {
    const interval = setInterval(() => {
      // Force re-render to update "X min ago" text
      if (lastUpdated) {
        setLastUpdated(new Date(lastUpdated)); // Trigger re-render
      }
    }, 60000); // Every 60 seconds

    return () => clearInterval(interval);
  }, [lastUpdated]);

  const handleOnboardingClose = () => {
    setShowOnboarding(false);
  };

  const loadPulseData = async () => {
    setLoading(true);
    setError(null);

    try {
      console.log('[PulseTab] Loading pulse data for group:', groupId);

      // Load all data in parallel for speed
      const [
        artifactsResult,
        messagesResult,
        marketSentimentResult,
        contributorsResult,
        rankingResult,
      ] = await Promise.all([
        getGroupArtifacts(groupId),
        getGroupMessages(groupId, 100), // Last 100 messages
        marketSentimentService.getMarketSentiment(),
        leaderboardService.getTopContributors(groupId, 3),
        leaderboardService.getGroupRanking(groupId),
      ]);

      // Handle errors
      if (artifactsResult.error || messagesResult.error) {
        setError('Failed to load pulse data');
        setLoading(false);
        return;
      }

      const messages = messagesResult.data || [];
      const marketSentiment = marketSentimentResult.data?.overall || 50; // Default neutral
      const topContributors = contributorsResult.data || [];
      const ranking = rankingResult.data;

      // Check if there are any messages with stock discussions
      const hasActivity = messages.length > 0;

      // Calculate group sentiment (average bullish percentage)
      // Returns null if no activity to show empty state
      const groupSentiment = hasActivity ? calculateGroupSentiment(messages) : null;

      // Calculate edge (divergence)
      // Only calculate if we have group sentiment
      const edge = groupSentiment !== null ? groupSentiment - marketSentiment : null;

      // Calculate trending stocks (last 7 days)
      const trendingStocks = calculateTrendingStocks(messages);

      // Calculate streak (consecutive days with activity)
      const streak = calculateStreak(messages);

      // Alpha (placeholder - requires prediction tracking)
      const alpha = 0;

      const pulseData: PulseData = {
        groupSentiment,
        marketSentiment,
        edge,
        trendingStocks,
        topContributors,
        groupRank: ranking?.rank || null,
        temperature: ranking?.temperature || 0,
        streak,
        alpha,
      };

      setPulse(pulseData);
      setLastUpdated(new Date());
      console.log('[PulseTab] Pulse data loaded:', pulseData);

      // Fetch historical sentiment data for chart
      console.log('[PulseTab] 📊 Fetching historical sentiment data...');
      const historicalResult = await historicalSentimentService.getHistoricalSentiment(groupId, 7);
      // Always set historical data (even if empty) so chart period selector is visible
      setHistoricalData({
        dataPoints: historicalResult.data?.dataPoints || [],
        averageEdge: historicalResult.data?.averageEdge || null,
      });
      console.log(
        '[PulseTab] 📊 Historical data loaded:',
        historicalResult.data?.dataPoints.length || 0,
        'points'
      );
    } catch (err) {
      console.error('[PulseTab] Error loading pulse data:', err);
      setError('Failed to analyze group');
    }

    setLoading(false);
  };

  const onRefresh = async () => {
    setRefreshing(true);
    await loadPulseData();
    setRefreshing(false);
  };

  // Convert time period to number of days
  const getPeriodDays = (period: TimePeriod): number => {
    switch (period) {
      case '1W':
        return 7;
      case '1M':
        return 30;
      case '3M':
        return 90;
      case '1Y':
        return 365;
      default:
        return 7;
    }
  };

  // Handle time period change from chart
  const handlePeriodChange = async (period: TimePeriod) => {
    setSelectedPeriod(period);
    const days = getPeriodDays(period);

    console.log('[PulseTab] 📊 Fetching historical data for period:', period, '(', days, 'days)');

    // Fetch historical sentiment data for selected period
    const historicalResult = await historicalSentimentService.getHistoricalSentiment(groupId, days);
    if (historicalResult.data) {
      setHistoricalData({
        dataPoints: historicalResult.data.dataPoints,
        averageEdge: historicalResult.data.averageEdge,
      });
      console.log(
        '[PulseTab] 📊 Historical data loaded:',
        historicalResult.data.dataPoints.length,
        'points'
      );
    }
  };

  // ============================================================================
  // Helper Functions
  // ============================================================================

  /**
   * Convert sentiment percentage to human-readable label
   */
  const getSentimentLabel = (sentiment: number): string => {
    if (sentiment >= 80) return 'Very Bullish';
    if (sentiment >= 60) return 'Bullish';
    if (sentiment >= 40) return 'Neutral';
    if (sentiment >= 20) return 'Bearish';
    return 'Very Bearish';
  };

  /**
   * Get sentiment emoji
   */
  const getSentimentEmoji = (sentiment: number): string => {
    if (sentiment >= 80) return '🚀';
    if (sentiment >= 60) return '📈';
    if (sentiment >= 40) return '→';
    if (sentiment >= 20) return '📉';
    return '🔴';
  };

  /**
   * Get edge interpretation
   */
  const getEdgeInterpretation = (edge: number): string => {
    if (edge >= 30) return 'Way ahead';
    if (edge >= 15) return 'Ahead of market';
    if (edge >= 5) return 'Slightly ahead';
    if (edge >= -5) return 'In sync';
    if (edge >= -15) return 'Contrarian';
    return 'Very contrarian';
  };

  /**
   * Format time ago (e.g., "2 min ago", "1 hour ago")
   */
  const getTimeAgo = (date: Date | null): string => {
    if (!date) return 'Just now';

    const seconds = Math.floor((new Date().getTime() - date.getTime()) / 1000);

    if (seconds < 60) return 'Just now';
    if (seconds < 120) return '1 min ago';
    if (seconds < 3600) return `${Math.floor(seconds / 60)} min ago`;
    if (seconds < 7200) return '1 hour ago';
    if (seconds < 86400) return `${Math.floor(seconds / 3600)} hours ago`;
    return 'Today';
  };

  const calculateGroupSentiment = (messages: Message[]): number => {
    // Count bullish vs bearish keywords in recent messages
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

    const recentMessages = messages.filter(msg => new Date(msg.created_at) >= sevenDaysAgo);

    if (recentMessages.length === 0) return 50; // Neutral

    let bullishCount = 0;
    let bearishCount = 0;

    const bullishKeywords = [
      'buy',
      'bullish',
      'moon',
      'rocket',
      'up',
      'gain',
      'profit',
      'long',
      'calls',
      'breakout',
    ];
    const bearishKeywords = [
      'sell',
      'bearish',
      'crash',
      'down',
      'loss',
      'short',
      'puts',
      'dump',
      'breakdown',
    ];

    recentMessages.forEach(msg => {
      const content = msg.content.toLowerCase();
      bullishKeywords.forEach(kw => {
        if (content.includes(kw)) bullishCount++;
      });
      bearishKeywords.forEach(kw => {
        if (content.includes(kw)) bearishCount++;
      });
    });

    const total = bullishCount + bearishCount;
    if (total === 0) return 50; // Neutral

    return Math.round((bullishCount / total) * 100);
  };

  const calculateTrendingStocks = (messages: Message[]): { ticker: string; mentions: number }[] => {
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

    const tickerMentions = new Map<string, number>();
    messages.forEach((msg: Message) => {
      const msgDate = new Date(msg.created_at);
      if (msgDate >= sevenDaysAgo) {
        const metadata = msg.metadata as {
          extracted_tickers?: string[];
          manual_tickers?: string[];
          tickers?: string[];
        };
        const tickers = [
          ...(metadata?.extracted_tickers || []),
          ...(metadata?.manual_tickers || []),
          ...(metadata?.tickers || []),
        ];
        tickers.forEach((ticker: string) => {
          tickerMentions.set(ticker, (tickerMentions.get(ticker) || 0) + 1);
        });
      }
    });

    return Array.from(tickerMentions.entries())
      .map(([ticker, mentions]) => ({ ticker, mentions }))
      .sort((a, b) => b.mentions - a.mentions)
      .slice(0, 10); // Top 10 for horizontal scroll
  };

  const calculateStreak = (messages: Message[]): number => {
    // Consecutive days with at least one message
    const dates = messages
      .map(m => new Date(m.created_at).toISOString().split('T')[0])
      .filter((date, index, self) => self.indexOf(date) === index)
      .sort()
      .reverse();

    let streak = 0;
    const today = new Date().toISOString().split('T')[0];
    let currentDate = new Date(today);

    for (const messageDate of dates) {
      const checkDate = currentDate.toISOString().split('T')[0];
      if (messageDate === checkDate) {
        streak++;
        currentDate.setDate(currentDate.getDate() - 1);
      } else {
        break;
      }
    }

    return streak;
  };

  // ============================================================================
  // Render States
  // ============================================================================

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={Colors.accent.primary} />
        <Text style={styles.loadingText}>Analyzing group...</Text>
      </View>
    );
  }

  if (error || !pulse) {
    return (
      <View style={styles.errorContainer}>
        <Ionicons name="alert-circle-outline" size={60} color={Colors.status.error} />
        <Text style={styles.errorTitle}>{error || 'Failed to load pulse'}</Text>
        <TouchableOpacity style={styles.retryButton} onPress={loadPulseData}>
          <Text style={styles.retryButtonText}>Retry</Text>
        </TouchableOpacity>
      </View>
    );
  }

  // Render temperature emoji (🔥 count)
  const temperatureEmoji = '🔥'.repeat(pulse.temperature || 0);

  return (
    <>
      {/* Onboarding Overlay (shows once on first visit) */}
      <PulseOnboardingOverlay visible={showOnboarding} onClose={handleOnboardingClose} />

      <ScrollView
        style={styles.container}
        contentContainerStyle={styles.content}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={onRefresh}
            tintColor={Colors.accent.primary}
          />
        }
      >
        {/* Ultra-Compact Header (40px) */}
        <View style={styles.header}>
          <Text style={styles.title}>PULSE</Text>
          <Text style={styles.subtitle}>{getTimeAgo(lastUpdated)}</Text>
          {pulse.groupRank && <Text style={styles.rank}>• #{pulse.groupRank}</Text>}
          {temperatureEmoji && <Text style={styles.temp}>• {temperatureEmoji}</Text>}

          {/* Help icon to show onboarding */}
          <TouchableOpacity
            onPress={() => setShowOnboarding(true)}
            style={styles.helpButton}
            hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
          >
            <Ionicons name="help-circle-outline" size={18} color={Colors.text.tertiary} />
          </TouchableOpacity>
        </View>

        {/* Trinity Boxes (80px) - Core Innovation */}
        <View style={styles.trinityRow}>
          {/* Group Sentiment */}
          <TouchableOpacity style={[styles.trinityBox, styles.groupBox]} activeOpacity={0.8}>
            <Text style={styles.trinityTitle}>YOUR GROUP</Text>
            {pulse.groupSentiment !== null ? (
              <>
                <Text style={styles.trinityNumber}>{pulse.groupSentiment}%</Text>
                <View style={styles.trinityMeta}>
                  <Text style={styles.trinityMetaText}>
                    {getSentimentEmoji(pulse.groupSentiment)}{' '}
                    {getSentimentLabel(pulse.groupSentiment)}
                  </Text>
                </View>
              </>
            ) : (
              <>
                <Text style={styles.trinityEmpty}>--</Text>
                <View style={styles.trinityMeta}>
                  <Text style={styles.trinityMetaText}>No data yet</Text>
                </View>
              </>
            )}
          </TouchableOpacity>

          {/* Market Sentiment */}
          <TouchableOpacity style={[styles.trinityBox, styles.marketBox]} activeOpacity={0.8}>
            <Text style={styles.trinityTitle}>MARKET</Text>
            <Text style={styles.trinityNumber}>{pulse.marketSentiment}%</Text>
            <View style={styles.trinityMeta}>
              <Text style={styles.trinityMetaText}>
                {getSentimentEmoji(pulse.marketSentiment)}{' '}
                {getSentimentLabel(pulse.marketSentiment)}
              </Text>
            </View>
          </TouchableOpacity>

          {/* Edge (Divergence) */}
          <TouchableOpacity
            style={[
              styles.trinityBox,
              styles.edgeBox,
              pulse.edge !== null && pulse.edge > 0
                ? styles.edgePositive
                : pulse.edge !== null && pulse.edge < 0
                  ? styles.edgeNegative
                  : styles.edgeNeutral,
            ]}
            activeOpacity={0.8}
          >
            <Text style={styles.trinityTitle}>YOUR EDGE</Text>
            {pulse.edge !== null ? (
              <>
                <Text style={styles.trinityNumber}>
                  {pulse.edge > 0 ? '+' : ''}
                  {Math.round(pulse.edge)}%
                </Text>
                <View style={styles.trinityMeta}>
                  <Text style={styles.trinityMetaText}>⚡ {getEdgeInterpretation(pulse.edge)}</Text>
                </View>
              </>
            ) : (
              <>
                <Text style={styles.trinityEmpty}>--</Text>
                <View style={styles.trinityMeta}>
                  <Text style={styles.trinityMetaText}>No data yet</Text>
                </View>
              </>
            )}
          </TouchableOpacity>
        </View>

        {/* Sentiment History Chart - Always show to allow period selection */}
        {historicalData && (
          <SentimentHistoryChart
            dataPoints={historicalData.dataPoints}
            averageEdge={historicalData.averageEdge}
            selectedPeriod={selectedPeriod}
            onPeriodChange={handlePeriodChange}
          />
        )}

        {/* Trending Stocks (32px) - Horizontal Scroll */}
        {pulse.trendingStocks.length > 0 && (
          <View style={styles.trendingSection}>
            <Text style={styles.sectionLabel}>TRENDING</Text>
            <ScrollView
              horizontal
              showsHorizontalScrollIndicator={false}
              style={styles.stockScroll}
            >
              {pulse.trendingStocks.map(stock => (
                <TouchableOpacity
                  key={stock.ticker}
                  style={styles.stockChip}
                  onPress={() => onTickerPress(stock.ticker)}
                  activeOpacity={0.7}
                >
                  <Text style={styles.stockTicker}>${stock.ticker}</Text>
                  <View style={styles.stockMentions}>
                    <Ionicons name="flame" size={10} color={Colors.market.profit} />
                    <Text style={styles.stockMentionsText}>{stock.mentions}</Text>
                  </View>
                </TouchableOpacity>
              ))}
            </ScrollView>
          </View>
        )}

        {/* Leaderboard (120px) - Dense Top 3 */}
        {pulse.topContributors.length > 0 && (
          <View style={styles.leaderboardSection}>
            <Text style={styles.sectionLabel}>TOP CONTRIBUTORS</Text>
            <View style={styles.leaderboard}>
              {pulse.topContributors.map((user, idx) => (
                <View key={user.userId} style={styles.leaderRow}>
                  <Text style={styles.medal}>{idx === 0 ? '🥇' : idx === 1 ? '🥈' : '🥉'}</Text>
                  <Text style={styles.userName} numberOfLines={1}>
                    {user.userName}
                  </Text>
                  <Text style={styles.userMessages}>{user.messageCount}</Text>
                  {user.isAccurate && <Text style={styles.badge}>🎯</Text>}
                  {user.isContrarian && <Text style={styles.badge}>⭐</Text>}
                </View>
              ))}
            </View>
          </View>
        )}

        {/* Performance Strip (32px) */}
        <View style={styles.performanceStrip}>
          <View style={styles.performanceRow}>
            <Text style={styles.performanceLabel}>Streak</Text>
            <Text style={styles.performanceValue}>{pulse.streak}d</Text>
          </View>
          <View style={styles.performanceRow}>
            <Text style={styles.performanceLabel}>Alpha</Text>
            <Text style={styles.performanceValue}>
              {pulse.alpha > 0 ? '+' : ''}
              {pulse.alpha.toFixed(1)}%
            </Text>
          </View>
        </View>

        {/* Upgrade CTA (48px) */}
        <TouchableOpacity style={styles.upgradeCTA} activeOpacity={0.8}>
          <Text style={styles.ctaText}>Unlock History & Full Leaderboard</Text>
          <Text style={styles.ctaIcon}>💎</Text>
        </TouchableOpacity>

        {/* Empty State - Shows when no trending stocks or contributors */}
        {pulse.topContributors.length === 0 && pulse.trendingStocks.length === 0 && (
          <View style={styles.emptyContainer}>
            <View style={styles.emptyIconContainer}>
              <Ionicons name="analytics-outline" size={48} color={Colors.ai.primary} />
            </View>
            <Text style={styles.emptyTitle}>Start Building Your Edge</Text>
            <Text style={styles.emptyText}>
              Pulse analyzes your group's discussions to reveal market insights and trends.
            </Text>

            {/* Action steps */}
            <View style={styles.emptySteps}>
              <View style={styles.emptyStep}>
                <View style={styles.emptyStepNumber}>
                  <Text style={styles.emptyStepNumberText}>1</Text>
                </View>
                <Text style={styles.emptyStepText}>
                  Mention stock tickers in chat ($AAPL, $TSLA)
                </Text>
              </View>

              <View style={styles.emptyStep}>
                <View style={styles.emptyStepNumber}>
                  <Text style={styles.emptyStepNumberText}>2</Text>
                </View>
                <Text style={styles.emptyStepText}>Share your bullish or bearish takes</Text>
              </View>

              <View style={styles.emptyStep}>
                <View style={styles.emptyStepNumber}>
                  <Text style={styles.emptyStepNumberText}>3</Text>
                </View>
                <Text style={styles.emptyStepText}>Watch Pulse reveal your group's edge</Text>
              </View>
            </View>

            {/* CTA to go back to chat */}
            <TouchableOpacity style={styles.emptyButton} activeOpacity={0.8}>
              <Ionicons name="chatbubbles" size={18} color={Colors.text.primary} />
              <Text style={styles.emptyButtonText}>Go to Chat</Text>
            </TouchableOpacity>
          </View>
        )}
      </ScrollView>
    </>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Colors.background.primary,
  },
  content: {
    flexGrow: 1,
    paddingHorizontal: 12, // Compact (was 16)
    paddingVertical: 8, // Compact (was 16)
    gap: 8, // Tight spacing
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    gap: 12,
  },
  loadingText: {
    fontSize: 14,
    color: Colors.text.secondary,
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 32,
  },
  errorTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: Colors.status.error,
    marginTop: 16,
    marginBottom: 24,
  },
  retryButton: {
    backgroundColor: Colors.accent.primary,
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 8,
  },
  retryButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: Colors.text.primary,
  },

  // ============================================================================
  // Ultra-Compact Header (40px)
  // ============================================================================
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    height: 40,
    gap: 8,
    backgroundColor: Colors.background.secondary,
    borderRadius: 8,
    paddingHorizontal: 12,
  },
  title: {
    fontSize: 16,
    fontWeight: '900',
    color: Colors.text.primary,
    letterSpacing: 1.2,
  },
  subtitle: {
    fontSize: 11,
    color: Colors.text.tertiary,
    textTransform: 'uppercase',
  },
  rank: {
    fontSize: 12,
    fontWeight: '700',
    color: Colors.accent.primary,
  },
  temp: {
    fontSize: 12,
    marginLeft: 'auto',
  },
  helpButton: {
    marginLeft: 4,
    padding: 4,
  },

  // ============================================================================
  // Trinity Boxes (80px) - CORE INNOVATION
  // ============================================================================
  trinityRow: {
    flexDirection: 'row',
    height: 80,
    gap: 6,
  },
  trinityBox: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 8,
    borderWidth: 2,
    paddingVertical: 6,
    paddingHorizontal: 4,
  },
  groupBox: {
    backgroundColor: `${Colors.market.profit}15`,
    borderColor: Colors.market.profit,
  },
  marketBox: {
    backgroundColor: `${Colors.text.tertiary}15`,
    borderColor: Colors.text.tertiary,
  },
  edgeBox: {
    borderWidth: 2,
  },
  edgePositive: {
    backgroundColor: `${Colors.ai.primary}15`,
    borderColor: Colors.ai.primary,
  },
  edgeNegative: {
    backgroundColor: `${Colors.status.error}15`,
    borderColor: Colors.status.error,
  },
  edgeNeutral: {
    backgroundColor: `${Colors.text.tertiary}15`,
    borderColor: Colors.text.tertiary,
  },
  trinityTitle: {
    fontSize: 8,
    fontWeight: '700',
    color: Colors.text.tertiary,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: 2,
  },
  trinityNumber: {
    fontSize: 22,
    fontWeight: '900',
    color: Colors.text.primary,
    letterSpacing: 0.5,
    marginBottom: 2,
  },
  trinityEmpty: {
    fontSize: 22,
    fontWeight: '900',
    color: Colors.text.tertiary,
    letterSpacing: 0.5,
    marginBottom: 2,
  },
  trinityMeta: {
    alignItems: 'center',
  },
  trinityMetaText: {
    fontSize: 8,
    fontWeight: '600',
    color: Colors.text.secondary,
    textAlign: 'center',
  },

  // ============================================================================
  // Trending Stocks (~44px total: 12px label + 32px scroll)
  // ============================================================================
  trendingSection: {
    // No fixed height - let it auto-size based on content
  },
  sectionLabel: {
    fontSize: 9,
    fontWeight: '700',
    color: Colors.text.tertiary,
    letterSpacing: 0.8,
    marginBottom: 6,
    textTransform: 'uppercase',
  },
  stockScroll: {
    flexDirection: 'row',
    height: 32, // Explicit height for horizontal ScrollView
  },
  stockChip: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: Colors.background.secondary,
    borderRadius: 6,
    paddingHorizontal: 10,
    paddingVertical: 4,
    marginRight: 6,
    gap: 6,
  },
  stockTicker: {
    fontSize: 12,
    fontWeight: '700',
    color: Colors.text.primary,
  },
  stockMentions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 3,
    backgroundColor: `${Colors.market.profit}20`,
    paddingHorizontal: 5,
    paddingVertical: 2,
    borderRadius: 4,
  },
  stockMentionsText: {
    fontSize: 9,
    fontWeight: '700',
    color: Colors.market.profit,
  },

  // ============================================================================
  // Leaderboard (120px) - Dense Top 3
  // ============================================================================
  leaderboardSection: {
    marginTop: 4,
  },
  leaderboard: {
    gap: 4,
  },
  leaderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: Colors.background.secondary,
    borderRadius: 6,
    paddingHorizontal: 10,
    paddingVertical: 8,
    height: 36,
    gap: 8,
  },
  medal: {
    fontSize: 16,
  },
  userName: {
    flex: 1,
    fontSize: 13,
    fontWeight: '600',
    color: Colors.text.primary,
  },
  userMessages: {
    fontSize: 12,
    fontWeight: '700',
    color: Colors.text.secondary,
    minWidth: 24,
    textAlign: 'right',
  },
  badge: {
    fontSize: 12,
  },

  // ============================================================================
  // Performance Strip (32px)
  // ============================================================================
  performanceStrip: {
    flexDirection: 'row',
    height: 32,
    gap: 6,
  },
  performanceRow: {
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: Colors.background.secondary,
    borderRadius: 6,
    paddingHorizontal: 10,
  },
  performanceLabel: {
    fontSize: 10,
    fontWeight: '600',
    color: Colors.text.tertiary,
    textTransform: 'uppercase',
  },
  performanceValue: {
    fontSize: 13,
    fontWeight: '900',
    color: Colors.text.primary,
  },

  // ============================================================================
  // Upgrade CTA (48px)
  // ============================================================================
  upgradeCTA: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    height: 48,
    backgroundColor: Colors.accent.primary,
    borderRadius: 8,
    gap: 8,
    marginTop: 4,
  },
  ctaText: {
    fontSize: 14,
    fontWeight: '700',
    color: Colors.text.primary,
    letterSpacing: 0.3,
  },
  ctaIcon: {
    fontSize: 16,
  },

  // ============================================================================
  // Empty State
  // ============================================================================
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 24,
    paddingTop: 32,
    paddingBottom: 32,
  },
  emptyIconContainer: {
    width: 80,
    height: 80,
    borderRadius: 40,
    backgroundColor: `${Colors.ai.primary}20`,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 20,
  },
  emptyTitle: {
    fontSize: 20,
    fontWeight: '700',
    color: Colors.text.primary,
    marginBottom: 8,
    textAlign: 'center',
  },
  emptyText: {
    fontSize: 14,
    color: Colors.text.secondary,
    textAlign: 'center',
    lineHeight: 20,
    marginBottom: 24,
  },
  emptySteps: {
    width: '100%',
    gap: 12,
    marginBottom: 24,
  },
  emptyStep: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
  },
  emptyStepNumber: {
    width: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: Colors.accent.primary,
    justifyContent: 'center',
    alignItems: 'center',
  },
  emptyStepNumberText: {
    fontSize: 12,
    fontWeight: '900',
    color: Colors.text.primary,
  },
  emptyStepText: {
    flex: 1,
    fontSize: 14,
    color: Colors.text.secondary,
    lineHeight: 20,
  },
  emptyButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: Colors.accent.primary,
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderRadius: 8,
  },
  emptyButtonText: {
    fontSize: 14,
    fontWeight: '700',
    color: Colors.text.primary,
  },
});
